import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  MainContext,
  RootAdminDashboardContext,
} from "components/utils/Contexts";
import { MdCorporateFare } from "react-icons/md";
import { AnimatePresence } from "framer-motion";
import { getCollectionRequest } from "services/apiRequests";
import SavedSuccessfulPanel from "components/main-view/utils/pop-up-panels/saved-successful-pop-up";
import RootAdminDashboardList from "./RootAdminDashboardList";
import { BrandListType } from "utils/interfaces";
import AddNewBrand from "components/main-view/utils/create-edit-brand";
import ViewHeader from "components/main-view/utils/view-header";
import { logoutFunction } from "services/authRequests";

interface RootAdminDashboardProps {
  ref: any;
  headerRef: React.MutableRefObject<any>;
  subHeaderRef: React.MutableRefObject<any>;
}

const RootAdminDashboard: React.FC<RootAdminDashboardProps> = forwardRef(
  ({ headerRef, subHeaderRef }, ref) => {
    // States definition
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedData, setSelectedData] = useState<BrandListType | null>(
      null
    );
    const [showSavedSuccessfully, setShowSavedSuccessfully] = useState(false);
    const [display, setDisplay] = useState<"main" | "create-edit">("main");
    const [version, setVersion] = useState(0);
    const [data, setData] = useState<BrandListType[]>([]);
    const [isLoadingData, setIsLoadingData] = useState(true);

    // Context variables
    const { setShowSideBarMenu } = useContext(MainContext);

    const addBrandRef: React.Ref<any> = useRef(null);

    useImperativeHandle(ref, () => ({
      getData() {
        getData();
      },
      setSelectedData(data: BrandListType | null) {
        setSelectedData(data);
      },
      setCreateBrand(bool: boolean) {
        setDisplay(bool ? "create-edit" : "main");
      },
      pressSaveButton() {
        if (addBrandRef.current) {
          addBrandRef.current.handleSaveBrand();
        }
      },
      onBackButtonClick() {
        onBackButtonClick();
      },
      onCreateEditBrand(title: string, description: string) {
        onCreateEditBrand(title, description);
      },
      onDiscard() {
        onDiscard();
      },
      handleSave() {
        handleSave();
      },
    }));

    useEffect(() => {
      onBackButtonClick();
      getData();
    }, []);

    const getData = async (filter = {}) => {
      setIsLoadingData(true);
      const response = await getCollectionRequest("/api/brands", [], filter);
      console.log(response);

      if (!response.successful) {
        if (response.logout) {
          return await logoutFunction();
        }
        return [];
      }

      setData(response.content);
      setIsLoadingData(false);
      return response.content;
    };

    /**
     * Function used to define the create edit a brand
     * @param title
     * @param description
     */
    const onCreateEditBrand = (title = "New Brand", description = "Brand") => {
      // Set the status for create edit brand
      setDisplay("create-edit");
      // And hide the pop up message
      setShowSavedSuccessfully(false);

      // (Main Header) Set titles and the back button
      setHeaderTitleAndDescription(title, description);
    };

    /**
     * Wrapper to transfor the clickEdit function into a handler
     */
    const handleClickEdit = (brand: BrandListType) => {
      return (event: React.MouseEvent) => {};
    };

    /**
     * Function used to set title and description
     * @param title
     * @param description
     */
    const setHeaderTitleAndDescription = (
      title: string,
      description: string,
      addIcon: boolean = true
    ) => {
      // Set the title and description
      headerRef.current.setHeaderTitle(title);
      headerRef.current.setHeaderDescription(
        addIcon ? (
          <div className="w-fit flex gap-1">
            <MdCorporateFare />
            <div>{description}</div>
          </div>
        ) : (
          description
        )
      );
    };

    /**
     * Function used when clicking back button on the header
     * @returns
     */
    const onBackButtonClick = () => {
      // Once pressed, come back to the root view
      onDiscardCreateEditBrand();
      // And force unselect data
      setSelectedData(null);
    };

    /**
     * Function that defines the root view
     */
    const onDiscard = () => {
      if (display === "create-edit") onDiscardCreateEditBrand();
    };

    /**
     * Function that defines discard behaviour when
     * creating/editing brands
     */
    const onDiscardCreateEditBrand = () => {
      // Activate the side bar menu
      setShowSideBarMenu(true);

      // And set the status
      setDisplay("main");
      // And force unselect data
      setSelectedData(null);

      // (Main Header) Change the titles and back button
      setHeaderTitleAndDescription("Brands", "List of current brands", false);
      headerRef.current.setShowBackButton(false);

      // (Dashboard Header) Change the button settings
      // Delete the generate report button
      headerRef.current.options().setShowGenerateReportsButton(false);
      // Activating the create new button
      headerRef.current.options().setShowCreateNewButton(true);
      // And turn off the other buttons
      headerRef.current
        .options()
        .setSaveButton({ show: false, disabled: true });

      // And force hide the subheader
      subHeaderRef.current.setShow(false);
    };

    /**
     * Function that coordinates the save function based on
     * the internal state of the component
     */
    const handleSave = () => {
      if (addBrandRef.current) addBrandRef.current.handleSaveBrand();
    };

    const afterSaveFunction = async () => {
      setVersion((currentVersion) => currentVersion + 1);
      setShowSavedSuccessfully(true);
      onDiscardCreateEditBrand();
      await getData();
    };

    return (
      <RootAdminDashboardContext.Provider value={{ version, setVersion }}>
        <div className="w-full self-stretch flex-col justify-start items-start gap-4 inline-flex">
          {display === "main" ? (
            <>
              <ViewHeader
                searchQuery={searchQuery}
                activeItem={""}
                tabOptions={[]}
                showTabOptions={false}
                setActiveItem={() => {}}
                setSearchQuery={setSearchQuery}
                headerRef={headerRef}
              />
              <RootAdminDashboardList
                data={data}
                searchQuery={searchQuery}
                isLoadingData={isLoadingData}
                handleClickEdit={handleClickEdit}
                onCreateEditBrand={onCreateEditBrand}
              />
            </>
          ) : (
            <AddNewBrand
              ref={addBrandRef}
              afterSaveFunction={afterSaveFunction}
              // selectedData={selectedData}
              headerRef={headerRef}
            />
          )}
          <AnimatePresence>
            {showSavedSuccessfully && (
              <SavedSuccessfulPanel
                title="Brand Added Successfully"
                paragraphs={[
                  "The new brand has been successfully added to the system.",
                  "Feel free to dive in to start customizing the brand's profile.",
                ]}
                createText="Add another"
                onCreateAnother={() => onCreateEditBrand()}
                onClose={() => setShowSavedSuccessfully(false)}
              />
            )}
          </AnimatePresence>
        </div>
      </RootAdminDashboardContext.Provider>
    );
  }
);

export default RootAdminDashboard;
